import type { ReactNode } from 'react';
import {
  PublicPageFooter,
  PublicPageHeader,
  SkipToMainContent,
} from '@/components/public/PublicPageChrome';

export type LegalPageId = 'privacy' | 'terms';

export type LegalSectionLink = {
  id: string;
  label: string;
};

type LegalDate = {
  label: 'Effective' | 'Last updated';
  dateTime: string;
  display: string;
};

export function LegalPageLayout({
  page,
  title,
  dates,
  sections,
  intro,
  children,
}: {
  page: LegalPageId;
  title: string;
  dates: LegalDate[];
  sections: readonly LegalSectionLink[];
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-surface-0 font-prose text-text-primary">
      <SkipToMainContent />
      <PublicPageHeader active={page} />

      <main
        id="main-content"
        tabIndex={-1}
        className="min-w-0 flex-1 outline-none"
      >
        <article className="mx-auto max-w-[760px] min-w-0 px-6 py-14 max-mobile:px-4 max-mobile:py-10">
          <LegalDates title={title} dates={dates} />
          <h1 className="mt-3 min-w-0 font-prose text-2xl font-semibold leading-tight text-text-primary [overflow-wrap:anywhere] max-mobile:text-xl">
            {title}
          </h1>
          <div className="mt-5 min-w-0 font-prose text-md leading-relaxed text-text-secondary [&_a]:break-words [&_a]:underline [&_a]:[overflow-wrap:anywhere] [&_code]:break-all [&_p]:m-0 [&_p]:[overflow-wrap:anywhere]">
            {intro}
          </div>

          <LegalPageNavigation title={title} sections={sections} />
          {children}
        </article>
      </main>

      <PublicPageFooter active={page} width="reading" />
    </div>
  );
}

function LegalDates({
  title,
  dates,
}: {
  title: string;
  dates: LegalDate[];
}) {
  return (
    <dl
      aria-label={`${title} dates`}
      className="m-0 flex min-w-0 flex-wrap gap-x-5 gap-y-1 font-ui text-xs uppercase tracking-[0.06em] text-text-tertiary"
    >
      {dates.map((date) => (
        <div key={date.label} className="flex min-w-0 flex-wrap gap-1">
          <dt>{date.label}</dt>
          <dd className="m-0">
            <time dateTime={date.dateTime}>{date.display}</time>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function LegalPageNavigation({
  title,
  sections,
}: {
  title: string;
  sections: readonly LegalSectionLink[];
}) {
  return (
    <nav
      aria-label={`${title} sections`}
      className="mt-7 min-w-0 rounded-lg border border-border bg-surface-1 p-4 max-mobile:p-3"
    >
      <p className="m-0 font-ui text-xs font-semibold uppercase tracking-[0.06em] text-text-tertiary">
        On this page
      </p>
      <ol className="m-0 mt-2 grid min-w-0 list-none grid-cols-2 gap-x-5 gap-y-1 p-0 max-mobile:grid-cols-1">
        {sections.map((section, index) => (
          <li key={section.id} className="min-w-0">
            <a
              href={`#${section.id}`}
              onClick={() => document.getElementById(section.id)?.focus()}
              className="inline-flex min-h-10 min-w-0 items-center font-ui text-sm text-navy-500 underline-offset-2 [overflow-wrap:anywhere] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-500"
            >
              <span className="mr-2 shrink-0 text-text-tertiary" aria-hidden="true">
                {index + 1}.
              </span>
              {section.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function LegalSection({
  id,
  n,
  title,
  children,
}: {
  id: string;
  n: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-10 min-w-0">
      <h2
        id={id}
        tabIndex={-1}
        className="m-0 flex min-w-0 scroll-mt-4 items-baseline font-prose text-lg font-semibold text-text-primary outline-none [overflow-wrap:anywhere]"
      >
        <span
          className="mr-3 shrink-0 font-ui text-sm text-navy-500"
          aria-hidden="true"
        >
          {n}
        </span>
        <span className="min-w-0">{title}</span>
      </h2>
      <div className="mt-3 min-w-0 font-prose text-base leading-relaxed text-text-secondary [&_a]:break-words [&_a]:underline [&_a]:[overflow-wrap:anywhere] [&_code]:break-all [&_li]:mt-1 [&_li]:[overflow-wrap:anywhere] [&_p:first-child]:mt-0 [&_p]:mt-3 [&_p]:[overflow-wrap:anywhere] [&_strong]:font-semibold [&_strong]:text-text-primary [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
